import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertCircle, CreditCard, CheckCircle, XCircle, Loader2, Smartphone } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import nexiSoftposService from '@/plugins/nexiSoftpos';
import { useTranslation } from 'react-i18next';

/**
 * Card Payment Flow Component
 * 
 * Handles NFC card payments via Nexi SoftPOS
 * Used in delivery completion flow
 */
const CardPaymentFlow = ({ 
  amount, 
  currency = 'EUR', 
  orderId, 
  onSuccess, 
  onCancel,
  onError 
}) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState('idle'); // idle, checking, ready, processing, success, failed, cancelled
  const [isNexiInstalled, setIsNexiInstalled] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    checkNexiAvailability();
  }, []);

  const checkNexiAvailability = async () => {
    setStatus('checking');
    
    const result = await nexiSoftposService.initialize();
    
    setIsNexiInstalled(result.installed);
    
    if (result.installed) {
      setStatus('ready');
    } else {
      setStatus('failed');
      setErrorMessage(t('pos.appNotInstalled'));
    }
  };

  const handleStartPayment = async () => {
    setStatus('processing');
    setErrorMessage('');
    
    try {
      const paymentData = {
        amount: amount.toString(),
        currency,
        reference: `ORDER_${orderId}_${Date.now()}`,
        merchantId: '', // TODO: Add from config
        terminalId: ''  // TODO: Add from config
      };
      
      const result = await nexiSoftposService.startPayment(paymentData);
      
      setPaymentResult(result);
      
      if (result.success) {
        setStatus('success');
        
        // Callback with payment data
        if (onSuccess) {
          onSuccess({
            transactionId: result.transactionId,
            amount,
            currency,
            reference: paymentData.reference,
            timestamp: new Date().toISOString()
          });
        }
      } else {
        if (result.status === 'cancelled') {
          setStatus('cancelled');
          if (onCancel) {
            onCancel();
          }
        } else {
          setStatus('failed');
          setErrorMessage(result.message || t('payment.failed'));
          if (onError) {
            onError(result);
          }
        }
      }
      
    } catch (error) {
      console.error('Payment error:', error);
      setStatus('failed');
      setErrorMessage(error.message || t('payment.errorOccurred'));
      if (onError) {
        onError({ error: error.message });
      }
    }
  };

  const handleCancel = async () => {
    await nexiSoftposService.cancelPayment();
    setStatus('cancelled');
    if (onCancel) {
      onCancel();
    }
  };

  const formatAmount = (amt) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: currency
    }).format(amt);
  };

  // Checking status
  if (status === 'checking') {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardContent className="p-8 text-center">
          <Loader2 className="h-12 w-12 mx-auto mb-4 animate-spin text-primary" />
          <p className="text-lg">{t('pos.checking')}</p>
        </CardContent>
      </Card>
    );
  }

  // Nexi not installed
  if (status === 'failed' && !isNexiInstalled) {
    return (
      <Card className="w-full max-w-md mx-auto border-red-500">
        <CardContent className="p-8 text-center">
          <AlertCircle className="h-16 w-16 mx-auto mb-4 text-red-500" />
          <h3 className="text-xl font-bold mb-4">{t('pos.notAvailable')}</h3>
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>
              {errorMessage}
            </AlertDescription>
          </Alert>
          <p className="text-muted-foreground mb-6">
            {t('pos.useCashAlternative')}
          </p>
          <Button onClick={onCancel} className="w-full">
            {t('pos.backToCash')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Ready to pay
  if (status === 'ready' || status === 'idle') {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">{t('pos.cardPayment')}</CardTitle>
        </CardHeader>
        <CardContent className="p-8">
          {/* Amount Display */}
          <div className="text-center mb-8">
            <p className="text-muted-foreground mb-2">{t('pos.amountToPay')}:</p>
            <p className="text-5xl font-bold text-primary">
              {formatAmount(amount)}
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              {t('pos.order')}: #{orderId}
            </p>
          </div>

          {/* Instructions */}
          <Alert className="mb-6 bg-blue-500/10 border-blue-500">
            <Smartphone className="h-5 w-5" />
            <AlertDescription>
              <strong>{t('pos.howItWorks')}:</strong>
              <ol className="list-decimal ml-4 mt-2 space-y-1">
                <li>{t('pos.step1')}</li>
                <li>{t('pos.step2')}</li>
                <li>{t('pos.step3')}</li>
                <li>{t('pos.step4')}</li>
              </ol>
            </AlertDescription>
          </Alert>

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button 
              onClick={handleStartPayment}
              className="w-full h-14 text-lg"
              size="lg"
            >
              <CreditCard className="h-6 w-6 mr-2" />
              {t('pos.startPayment')}
            </Button>
            
            <Button 
              onClick={onCancel}
              variant="outline"
              className="w-full"
            >
              {t('pos.cancel')}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Processing
  if (status === 'processing') {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardContent className="p-8 text-center">
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-24 w-24 rounded-full border-4 border-primary border-t-transparent animate-spin"></div>
            </div>
            <Smartphone className="h-16 w-16 mx-auto mt-4 text-primary animate-pulse" />
          </div>
          
          <h3 className="text-2xl font-bold mb-4">{t('pos.processing')}</h3>
          <p className="text-lg text-muted-foreground mb-2">
            {t('pos.holdCard')}<br />
            {t('pos.holdCardLine2')}
          </p>
          <p className="text-sm text-muted-foreground mt-6">
            {t('payment.amount')}: {formatAmount(amount)}
          </p>
          
          <Button 
            onClick={handleCancel}
            variant="ghost"
            className="mt-6"
          >
            {t('pos.cancel')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Success
  if (status === 'success') {
    return (
      <Card className="w-full max-w-md mx-auto border-green-500">
        <CardContent className="p-8 text-center">
          <div className="relative mb-6">
            <div className="h-24 w-24 mx-auto rounded-full bg-green-500/20 flex items-center justify-center">
              <CheckCircle className="h-16 w-16 text-green-500" />
            </div>
          </div>
          
          <h3 className="text-2xl font-bold mb-4 text-green-500">
            {t('pos.paymentSuccess')}
          </h3>
          
          <div className="mb-6">
            <p className="text-3xl font-bold">
              {formatAmount(amount)}
            </p>
            {paymentResult?.transactionId && (
              <p className="text-xs text-muted-foreground mt-2">
                {t('payment.transactionId')}: {paymentResult.transactionId}
              </p>
            )}
          </div>
          
          <Alert className="bg-green-500/10 border-green-500">
            <CheckCircle className="h-5 w-5" />
            <AlertDescription>
              {t('pos.successMessage')}
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  // Failed
  if (status === 'failed') {
    return (
      <Card className="w-full max-w-md mx-auto border-red-500">
        <CardContent className="p-8 text-center">
          <XCircle className="h-16 w-16 mx-auto mb-4 text-red-500" />
          
          <h3 className="text-2xl font-bold mb-4">{t('pos.paymentFailed')}</h3>
          
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>
              {errorMessage || t('payment.failed')}
            </AlertDescription>
          </Alert>
          
          <div className="space-y-3">
            <Button 
              onClick={handleStartPayment}
              className="w-full"
            >
              {t('pos.retry')}
            </Button>
            
            <Button 
              onClick={onCancel}
              variant="outline"
              className="w-full"
            >
              {t('pos.switchToCash')}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Cancelled
  if (status === 'cancelled') {
    return (
      <Card className="w-full max-w-md mx-auto border-yellow-500">
        <CardContent className="p-8 text-center">
          <AlertCircle className="h-16 w-16 mx-auto mb-4 text-yellow-500" />
          
          <h3 className="text-2xl font-bold mb-4">{t('pos.paymentCancelled')}</h3>
          
          <p className="text-muted-foreground mb-6">
            {t('pos.cancelledMessage')}
          </p>
          
          <div className="space-y-3">
            <Button 
              onClick={handleStartPayment}
              className="w-full"
            >
              {t('pos.retry')}
            </Button>
            
            <Button 
              onClick={onCancel}
              variant="outline"
              className="w-full"
            >
              {t('pos.switchToCash')}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return null;
};

export default CardPaymentFlow;
